import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Header } from "../../components/layout";
import { Card, Pagination } from "../../components/common";
import {
  IdentityTable,
  IdentityFilters,
  IdentityDetail,
  IdentityStats,
  DarkWebIdentityTable,
} from "../../components/modules/identities";
import { identitiesToCSV, downloadFile, identitiesToPDF } from "../../utils/helpers";
import "./Identities.css";
import MfaBreakdown from "../../components/modules/identities/MfaBreakdown/MfaBreakdown";
import MfaUsersModal from "../../components/modules/identities/MfaUsersModal/MfaUsersModal";
import { AlertCircle } from "lucide-react";
import { FaSync } from "react-icons/fa";

// Redux actions
import {
  fetchIdentities,
  fetchMfaBreakdown,
  fetchMfaMethodUsers,
  fetchIdentityRemediation,
  fetchIdentityDarkWebDetails,
  setFilters,
  setSearch,
  resetFilters,
  clearCache,
  clearMfaMethodUsersCache,
  setMfaMethodUsersPage,
  setMfaMethodUsersPageSize,
  setMfaMethodUsersSearch,
  clearCurrentRemediation,
  clearCurrentdarkwebDetails,
  setPage,
  setPageSize,
} from "../../store/slices/identitiesSlice";
import { fetchStats } from "../../store/slices/statsSlice";
import DarkWebDetailsModal from "../../components/modules/identities/DarkWebDetailsModal/darkWebDetailsModal";

/**
 * Identities Page
 * Main page for viewing and managing all identities
 * Handles modal and data fetching with Redux
 */
const Identities = () => {
  const dispatch = useDispatch();
  const initialFetchDone = useRef(false);

  // Redux selectors
  const {
    identities,
    pagination,
    darkwebPagination,
    loading,
    error,
    filters,
    mfa,
    mfaMethodUsers,
    darkwebIdentityDetails,
    currentRemediation,
  } = useSelector((state) => state.identities);

  const { data: stats, loading: statsLoading } = useSelector(
    (state) => state.stats,
  );
  // const {darkwebIdentityDetails} =useSelector((state)=>state.darkwebIdentityDetails)

  // Local state
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState(filters?.type || "human");

  /* ── Modal state ───────────────────────────── */
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState(null);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaUsersData, setMfaUsersData] = useState(null);

  /* ── Initial data fetch (runs only once) ───– */
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;

      // Fetch stats
      dispatch(fetchStats());
      const defaultFilters = { type: "human", account_type: null, page: 1 };
      dispatch(setFilters(defaultFilters));
      setActiveTab("human");
      dispatch(fetchIdentities(defaultFilters));
    }
  }, [dispatch]);

  /* ── Fetch identities when filters change ──– */
  // useEffect(() => {
  //   if (activeTab !== 'mfa' && initialFetchDone.current) {
  //     dispatch(fetchIdentities(filters));
  //   }
  // }, [dispatch, filters, activeTab]);
  useEffect(() => {
    if (!initialFetchDone.current) return;

    if (activeTab === "dark-web-monitoring") {
      dispatch(fetchIdentityDarkWebDetails(filters));
    } else if (activeTab !== "mfa") {
      dispatch(fetchIdentities(filters));
    }
  }, [dispatch, filters, activeTab]);

  /* ── Open modal with identity details ──────– */
  const openIdentityDetail = useCallback(
    (identity) => {
      setSelectedIdentity(identity);
      setIsModalOpen(true);

      const id = identity?.id || identity?.objectId;
      if (id) {
        dispatch(fetchIdentityRemediation(id));
      }
    },
    [dispatch],
  );

  /* ── Close modal ────────────────────────────– */
  const closeIdentityDetail = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIdentity(null);
    dispatch(clearCurrentRemediation());
  }, [dispatch]);

  // Open MFA users modal
  const openMfaUsersModal = useCallback(
    async (method) => {
      setSelectedMfaMethod(method);
      setIsMfaModalOpen(true);
      dispatch(setMfaMethodUsersSearch(''));
      dispatch(fetchMfaMethodUsers({ method, page: 1, limit: 5 }));
    },
    [dispatch]
  );

  // Close MFA users modal
  const closeMfaUsersModal = useCallback(() => {
    setIsMfaModalOpen(false);
  }, []);


  const handleMfaUsersPageChange = useCallback(
    (newPage) => {
      if (!selectedMfaMethod) return;
      dispatch(setMfaMethodUsersPage(newPage));
      dispatch(
        fetchMfaMethodUsers({
          method: selectedMfaMethod,
          page: newPage,
          limit: mfaMethodUsers?.pagination?.pageSize,
          search: mfaMethodUsers.search,
        })
      );
    },
    [dispatch, selectedMfaMethod, mfaMethodUsers?.pagination?.pageSize]
  );

  const handleMfaUsersPageSizeChange = useCallback(
    (newSize) => {
      if (!selectedMfaMethod) return;
      dispatch(setMfaMethodUsersPageSize(newSize));
      dispatch(
        fetchMfaMethodUsers({
          method: selectedMfaMethod,
          page: 1,
          limit: newSize,
          search: mfaMethodUsers.search,
        })
      );
    },
    [dispatch, selectedMfaMethod]
  );

  const handleMfaUsersSearch = useCallback(
    (value) => {
      if (!selectedMfaMethod) return;
      dispatch(setMfaMethodUsersSearch(value));
      dispatch(
        fetchMfaMethodUsers({
          method: selectedMfaMethod,
          page: 1, // Always reset to page 1 on search
          limit: mfaMethodUsers.pagination.pageSize,
          search: value,
        })
      );
    },
    [dispatch, selectedMfaMethod, mfaMethodUsers?.pagination?.pageSize]
  );

  /* ── Handle search ─────────────────────────– */
  const handleSearch = useCallback(
    (value) => {
      setSearchValue(value);
      dispatch(setSearch(value));
    },
    [dispatch],
  );

  /* ── Handle export ─────────────────────────– */
  // const handleExport = useCallback(() => {
  //   if (identities && identities.length > 0) {
  //     const csv = identitiesToCSV(identities);
  //     const filename = `identities-export-${new Date().toISOString().split("T")[0]
  //       }.csv`;
  //     downloadFile(csv, filename);
  //   }
  // }, [identities]);

  const handleExport = useCallback(() => {
    if (identities?.length > 0) {
      identitiesToPDF(identities);
    }
  }, [identities]);


  /* ── Handle tab change ─────────────────────– */
  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      if (tab === "human") {
        dispatch(setFilters({ type: "human", account_type: null, page: 1 }));
      } else if (tab === "non-human") {
        dispatch(
          setFilters({ type: "non-human", account_type: null, page: 1 }),
        );
      } else if (tab === "mfa") {
        dispatch(fetchMfaBreakdown());
      } else if (tab === "dark-web-monitoring") {
        dispatch(setFilters({ page: 1 }));
      }
    },
    [dispatch],
  );

  /* ── Handle filter change ──────────────────– */
  const handleFilterChange = useCallback(
    (newFilters) => {
      // Reset to page 1 when filters change, preserve type and limit
      dispatch(
        setFilters({
          ...newFilters,
          page: 1,
        }),
      );
    },
    [dispatch],
  );

  /* ── Handle reset filters ──────────────────– */
  const handleResetFilters = useCallback(() => {
    dispatch(resetFilters());
    setSearchValue("");
  }, [dispatch]);

  /* ── Handle refresh ────────────────────────– */
  const handleRefresh = useCallback(() => {
    dispatch(clearCache());
    if (activeTab === "dark-web-monitoring") {
      dispatch(fetchIdentityDarkWebDetails(filters));
    }
    if (activeTab === "human") {
      dispatch(setFilters({ type: "human", account_type: null, page: 1 }));
    } else if (activeTab === "non-human") {
      dispatch(setFilters({ type: "non-human", account_type: null, page: 1 }));
    } else {
      dispatch(fetchIdentities(filters));
    }
  }, [dispatch]);

  /* ── Handle page change ────────────────────– */
  const handlePageChange = useCallback(
    (newPage) => {
      dispatch(setPage(newPage));
    },
    [dispatch],
  );

  /* ── Handle page size change ───────────────– */
  const handlePageSizeChange = useCallback(
    (newSize) => {
      dispatch(setPageSize(newSize));
    },
    [dispatch],
  );

  const tabs = [
    { id: "human", label: "Human" },
    { id: "non-human", label: "Non-Human" },
    { id: "mfa", label: "MFA Distribution" },
    { id: "dark-web-monitoring", label: "Dark Web Monitoring" },
  ];

  /* ──────────────────────────────────────────– */
  return (
    <div className="identities-page">
      {/* ── Header ────────────────────────────– */}
      <Header
        title="All Identities"
        subtitle="View and manage all human and non-human identities"
        showSearch={activeTab === 'human' || activeTab === 'non-human'}
        showSync={false}
        showExport={activeTab === 'human' || activeTab === 'non-human'}
        searchValue={(activeTab === 'human' || activeTab === 'non-human') ? searchValue : ""}
        onSearch={handleSearch}
        onExport={handleExport}
      />

      {/* ── Error banner ──────────────────────– */}
      {error && (
        <div className="identity-page__error" role="alert">
          <div className="identity-page__error-content">
            <AlertCircle size={18} aria-hidden="true" />
            <span>{error}</span>
          </div>
          <button
            className="identity-page__error-retry"
            onClick={handleRefresh}
            aria-label="Retry loading identities"
          >
            <FaSync size={12} aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {/* ── Statistics ────────────────────────– */}
      <IdentityStats stats={stats} loading={statsLoading} />

      {/* ── Tabs ──────────────────────────────– */}
      <nav className="identity-tabs-modern" aria-label="Identity category tabs">
        <div className="identity-tabs-scroll" role="tablist">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              aria-controls={`tabpanel-${id}`}
              id={`tab-${id}`}
              className={`identity-tab-btn${activeTab === id ? " active" : ""}`}
              onClick={() => handleTabChange(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>
      {/* main content */}
      <Card padding={false}>
        {(() => {
          switch (activeTab) {
            case "mfa":
              return (
                <div
                  role="tabpanel"
                  id="tabpanel-mfa"
                  aria-labelledby="tab-mfa"
                >
                  <MfaBreakdown
                    data={mfa.data}
                    loading={mfa.loading}
                    onMfaRowClick={openMfaUsersModal}
                  />
                </div>
              );

            case "dark-web-monitoring":
              return (
                <div
                  role="tabpanel"
                  id="tabpanel-dark-web-monitoring"
                  aria-labelledby="tab-dark-web-monitoring"
                >
                  <DarkWebIdentityTable
                    darkwebIdentities={darkwebIdentityDetails?.data} // Pass data mapped via updated filters
                    loading={darkwebIdentityDetails?.loading}
                  />
                  {darkwebPagination && darkwebPagination.totalPages > 1 && (
                    <Pagination
                      currentPage={darkwebPagination.currentPage}
                      totalPages={darkwebPagination.totalPages}
                      totalItems={darkwebPagination.totalCount}
                      itemsPerPage={darkwebPagination.pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  )}
                </div>
              );

            default: // Covers 'human' and 'non-human' tabs
              return (
                <div
                  role="tabpanel"
                  id={`tabpanel-${activeTab}`}
                  aria-labelledby={`tab-${activeTab}`}
                >
                  <IdentityFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={handleResetFilters}
                    showAccountTypeFilter={activeTab === "human"}
                  />
                  <IdentityTable
                    identities={identities}
                    loading={loading}
                    onRowClick={openIdentityDetail}
                    onViewDetails={openIdentityDetail}
                  />
                  {pagination && pagination?.totalPages > 1 && (
                    <Pagination
                      currentPage={pagination?.currentPage}
                      totalPages={pagination?.totalPages}
                      totalItems={pagination?.totalCount}
                      itemsPerPage={pagination?.pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  )}
                </div>
              );
          }
        })()}
      </Card>

      {/* ── Modal (single instance) ───────────– */}
      <IdentityDetail
        identity={selectedIdentity}
        isOpen={isModalOpen}
        onClose={closeIdentityDetail}
        remediation={currentRemediation.data}
        loading={currentRemediation.loading}
      />

      {/* ── MFA Users Modal ──────────────────– */}
      <MfaUsersModal
        isOpen={isMfaModalOpen}
        onClose={closeMfaUsersModal}
        method={selectedMfaMethod}
        mfaData={mfaMethodUsers.data}
        loading={mfaMethodUsers.loading}
        pagination={mfaMethodUsers.pagination}
        onPageChange={handleMfaUsersPageChange}
        onPageSizeChange={handleMfaUsersPageSizeChange}
        onSearch={handleMfaUsersSearch}          // ← ADD
        searchValue={mfaMethodUsers.search}      // ← ADD (controlled from Redux)
      />
    </div>
  );
};

export default Identities;
